/**
 * Date utility functions for interview scheduling
 */

/**
 * Format time string to readable format
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time (e.g., "9:00 AM")
 */
export function formatTime(time) {
  const [hours, minutes] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  
  const hours12 = date.getHours() % 12 || 12;
  const ampm = date.getHours() >= 12 ? "PM" : "AM";
  const mins = minutes.toString().padStart(2, "0");
  
  return `${hours12}:${mins} ${ampm}`;
}

/**
 * Format date string to readable format
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} Formatted date (e.g., "Mon, Jan 15, 2024")
 */
export function formatDate(dateStr) {
  const date = new Date(dateStr + "T00:00:00");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  const day = days[date.getDay()];
  const month = months[date.getMonth()];
  const dayNum = date.getDate();
  const year = date.getFullYear();
  
  return `${day}, ${month} ${dayNum}, ${year}`;
}

/**
 * Format date for display in candidate preferences
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} Formatted date (e.g., "Mon, Jan 15")
 */
export function formatDateDisplay(dateStr) {
  try {
    const date = new Date(dateStr + "T00:00:00");
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    const day = days[date.getDay()];
    const month = months[date.getMonth()];
    const dayNum = date.getDate();
    
    return `${day}, ${month} ${dayNum}`;
  } catch {
    return dateStr;
  }
}

/**
 * Check if a date is in the past
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export function isPastDate(dateStr) {
  const date = parseLocalDate(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string}
 */
export function getTodayDate() {
  const today = new Date();
  return formatDateToYYYYMMDD(today);
}

/**
 * Parse date string in YYYY-MM-DD format to Date object using local time
 * This prevents timezone issues where dates shift by one day
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {Date} Date object in local time
 */
export function parseLocalDate(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a Date object to YYYY-MM-DD string using local time (not UTC)
 * This prevents timezone issues where dates shift by one day
 * @param {Date} date - Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatDateToYYYYMMDD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
